import { json } from "@codemirror/lang-json";
import {
  Alert,
  Button,
  Flex,
  Group,
  Input,
  Select,
  Stack,
  Textarea,
  useComputedColorScheme,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { CheckIcon, XCircleIcon } from "@phosphor-icons/react";
import { xcodeDark, xcodeLight } from "@uiw/codemirror-theme-xcode";
import CodeMirror, { ReactCodeMirrorRef } from "@uiw/react-codemirror";
import pako from "pako";
import { useEffect, useRef, useState } from "react";
import { z } from "zod/v4";

import { availableVersions, OcpiSchemas } from "../core/ocpi.schemas";
import type { OcpiVersion } from "../core/ocpi.version";
import { ThemeToggle } from "./ThemeToggle";

export function App() {
  let didInit = false;
  const computedColorScheme = useComputedColorScheme("light", {
    getInitialValueInEffect: true,
  });
  const ocpiInputRef = useRef<ReactCodeMirrorRef>(null);
  const responseInputRef = useRef<HTMLTextAreaElement>(null);
  const [validationCompleted, setValidationCompleted] = useState(false);
  const [issues, setIssues] = useState<z.core.$ZodIssue[]>([]);
  const [errors, setErrors] = useState("");

  const searchParams = new URLSearchParams(window.location.search);

  const form = useForm({
    mode: "uncontrolled",
    initialValues: {
      ocpiInput: decode(searchParams.get("ocpiInput") ?? ""),
      ocpiVersion: (searchParams.get("ocpiVersion") as OcpiVersion) ?? "",
      schemaName: searchParams.get("schemaName") ?? "",
    },
    validate: {
      ocpiInput: (value) => (!value ? "OCPI input is required" : null),
      ocpiVersion: (value) => (!value ? "OCPI version is required" : null),
      schemaName: (value) => (!value ? "Schema name is required" : null),
    },
    validateInputOnChange: true,
  });

  useEffect(() => {
    if (!didInit) {
      didInit = true;
      validateForm();
    }
  }, []);

  const validateForm = async () => {
    setIssues([]);
    setErrors("");

    if (!form.isValid()) {
      return;
    }

    setValidationCompleted(false);
    form.setSubmitting(true);

    try {
      const ocpiInput = form.getValues().ocpiInput;
      const ocpiVersion = form.getValues().ocpiVersion as OcpiVersion;
      const schemaName = form.getValues().schemaName;

      const response = await fetch(new URL("/api/validate", location.href), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ ocpiInput, ocpiVersion, schemaName }),
      });

      const result = await response.json();

      setIssues(result.issues ?? []);
      setErrors(result.errorMessage ?? "");
      form.setSubmitting(false);
      setValidationCompleted(true);
      const newSearchParams = new URLSearchParams();

      if (form.getValues().ocpiInput) {
        newSearchParams.set("ocpiInput", encode(form.getValues().ocpiInput));
      }
      if (form.getValues().ocpiVersion) {
        newSearchParams.set("ocpiVersion", form.getValues().ocpiVersion);
      }
      if (form.getValues().schemaName) {
        newSearchParams.set("schemaName", form.getValues().schemaName);
      }

      // Update the URL without reloading the page
      window.history.replaceState(
        null,
        "",
        `${window.location.pathname}?${newSearchParams}`,
      );
    } catch (error) {
      setErrors(String(error));
      form.setSubmitting(false);
      setValidationCompleted(true);
    }
  };

  return (
    <Stack>
      <Group>
        <Flex flex={1} />
        <Button
          variant="default"
          size="xs"
          onClick={() => {
            // Force CodeMirror to reset the state
            ocpiInputRef.current?.view?.dispatch({
              changes: {
                from: 0,
                to: form.getValues().ocpiInput.length,
                insert: "",
              },
            });
            form.setFieldValue("ocpiInput", "");
            setIssues([]);
            setErrors("");
            setValidationCompleted(false);
          }}
        >
          Clear
        </Button>
        <ThemeToggle />
      </Group>
      <Input.Wrapper label="OCPI Input" required>
        <Group
          grow
          bd="1px solid gray.4"
          style={{ borderRadius: "4px", overflow: "auto" }}
        >
          <CodeMirror
            ref={ocpiInputRef}
            extensions={[json()]}
            height="400px"
            theme={computedColorScheme === "dark" ? xcodeDark : xcodeLight}
            basicSetup={{ foldGutter: true }}
            value={form.values.ocpiInput}
            key={form.key("ocpiInput")}
            {...form.getInputProps("ocpiInput")}
          />
        </Group>
      </Input.Wrapper>
      <Group grow>
        <Select
          required
          label="OCPI version"
          name="ocpiVersion"
          placeholder="Select OCPI version"
          data={availableVersions}
          key={form.key("ocpiVersion")}
          {...form.getInputProps("ocpiVersion")}
          onChange={(value) => {
            const newOcpiVersion = value as OcpiVersion;
            form.setFieldValue("ocpiVersion", newOcpiVersion);
            if (
              !value ||
              !OcpiSchemas[newOcpiVersion][form.getValues().schemaName]
            ) {
              form.setFieldValue("schemaName", ""); // Reset schema name on OCPI version change, if the selected OCPI version does not have currently selected schema
            }
          }}
        />
        <Select
          required
          clearable
          label="Schema name"
          name="schemaName"
          disabled={!form.getValues().ocpiVersion}
          placeholder={
            form.getValues().ocpiVersion
              ? "Select OCPI schema"
              : "Select OCPI version first"
          }
          searchable
          data={Object.keys(OcpiSchemas[form.getValues().ocpiVersion] ?? {})}
          key={form.key("schemaName")}
          {...form.getInputProps("schemaName")}
          error={null}
        />
      </Group>
      <Button
        color="solidstudio-blue"
        disabled={!form.isValid()}
        loading={form.submitting}
        onClick={validateForm}
      >
        Validate
      </Button>
      {issues.length > 0 && (
        <Alert
          variant="light"
          color="red"
          title={`${issues.length} error(s) detected`}
          icon={<XCircleIcon />}
        />
      )}
      {validationCompleted && issues.length === 0 && (
        <Alert
          variant="light"
          color="green"
          title={`Valid object. No errors found`}
          icon={<CheckIcon />}
        />
      )}
      {validationCompleted && issues.length > 0 && (
        <Textarea
          autosize
          placeholder="Validation result"
          minRows={2}
          resize="none"
          readOnly
          error={issues.length > 0}
          ref={responseInputRef}
          value={errors.replaceAll("\n✖", "\n\n✖")}
        />
      )}
    </Stack>
  );
}

const toBase64 = (str: string) =>
  btoa(str).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");

const fromBase64 = (str: string) => {
  str = str.replace(/-/g, "+").replace(/_/g, "/");
  while (str.length % 4) str += "=";
  return atob(str);
};

const encode = (payload: string) => {
  const compressed = pako.deflate(payload);
  const binaryStr = Array.from(compressed)
    .map((b) => String.fromCharCode(b))
    .join("");
  return toBase64(binaryStr);
};

const decode = (payload: string) => {
  const binaryStr = fromBase64(payload);
  const byteArray = Uint8Array.from(binaryStr, (c) => c.charCodeAt(0));
  return pako.inflate(byteArray, { to: "string" });
};
